// Password strength indicator. Pure visual — does not block submit. The submit
// handler in Register.tsx still enforces the hard floor (length >= 8).
//
// Score: 1-5 based on length + character-class diversity. Each unmet criterion
// is also surfaced so the user knows what to add.

import { useId } from "react";
import { Check, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

type Strength = "weak" | "fair" | "good" | "excellent";

const STRENGTH_LABEL: Record<Strength, string> = {
  weak:     "弱",
  fair:     "一般",
  good:     "良好",
  excellent: "強",
};

const STRENGTH_BAR_COLOR: Record<Strength, string> = {
  weak:     "bg-rose-500",
  fair:     "bg-amber-500",
  good:     "bg-emerald-500",
  excellent: "bg-emerald-500",
};

const STRENGTH_TEXT_COLOR: Record<Strength, string> = {
  weak:     "text-rose-600",
  fair:     "text-amber-600",
  good:     "text-emerald-600",
  excellent: "text-emerald-700",
};

export type PasswordAssessment = {
  /** 0..5. 0 when the input is empty. */
  score: number;
  /** Bucketed strength label. */
  strength: Strength;
  /** True if the password meets the minimum (length >= 8). */
  meetsMinimum: boolean;
  /** Criteria that the user could improve. */
  criteria: Array<{ key: string; label: string; met: boolean }>;
};

export function assessPassword(value: string): PasswordAssessment {
  const criteria: Array<{ key: string; label: string; met: boolean }> = [
    { key: "length8",  label: "至少 8 個字元", met: value.length >= 8 },
    { key: "length12", label: "12 個字元以上", met: value.length >= 12 },
    { key: "lower",    label: "包含小寫英文", met: /[a-z]/.test(value) },
    { key: "upper",    label: "包含大寫英文", met: /[A-Z]/.test(value) },
    { key: "digit",    label: "包含數字",     met: /\d/.test(value) },
  ];

  const score = criteria.filter(c => c.met).length;
  const meetsMinimum = value.length >= 8;

  let strength: Strength;
  if (score <= 1)      strength = "weak";
  else if (score <= 3) strength = "fair";
  else if (score === 4) strength = "good";
  else                 strength = "excellent";

  return { score, strength, meetsMinimum, criteria };
}

export function PasswordStrength({ value }: { value: string }) {
  const headingId = useId();
  // Empty input → don't render the meter (it'd flash red/empty on initial mount).
  if (value.length === 0) return null;

  const { score, strength, meetsMinimum, criteria } = assessPassword(value);
  const filled = Math.max(1, Math.min(4, score));
  const barColor = STRENGTH_BAR_COLOR[strength];
  const textColor = STRENGTH_TEXT_COLOR[strength];

  return (
    <div
      className="mt-2 space-y-2"
      role="group"
      aria-labelledby={headingId}
    >
      <div
        className="flex h-1.5 w-full gap-1 overflow-hidden rounded-full"
        aria-hidden
      >
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className={cn(
              "h-full flex-1 rounded-full transition-colors",
              i < filled ? barColor : "bg-muted",
            )}
          />
        ))}
      </div>

      <div className="flex items-center justify-between text-xs">
        <p
          id={headingId}
          className={cn("font-medium", textColor)}
          aria-live="polite"
        >
          密碼強度：{STRENGTH_LABEL[strength]}
          {!meetsMinimum && (
            <span className="ml-1 text-muted-foreground">（至少 8 個字元）</span>
          )}
        </p>
        <p className="text-[10px] text-muted-foreground tabular-nums">
          {score} / 5
        </p>
      </div>

      <ul className="grid grid-cols-1 gap-0.5 text-[11px] text-muted-foreground sm:grid-cols-2">
        {criteria.map(c => (
          <li
            key={c.key}
            className={cn(
              "flex items-center gap-1.5",
              c.met && "text-foreground/80",
            )}
          >
            {c.met ? (
              <Check className="h-3 w-3 text-emerald-600" aria-hidden />
            ) : (
              <Circle className="h-3 w-3 text-muted-foreground/50" aria-hidden />
            )}
            <span>{c.label}</span>
          </li>
        ))}
      </ul>

      <div
        className="sr-only"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={score}
        aria-valuetext={`密碼強度：${STRENGTH_LABEL[strength]}`}
      />
    </div>
  );
}